import { ChangeEvent, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  getThemeZipLength,
  validDisplayNameLengthRule,
  validDisplayNameRule,
  validStringRule,
  validZipCodeRule,
} from '../components/address-form/address-utils';
import {
  FieldType,
  FormFields,
  FormFieldSelectedList,
} from '../components/form-fields-generator/form-fields-generator-types';
import styles from '../components/form-fields-generator/form-fields-generator.module.scss';
import { getSelectedFormFields } from '../components/form-fields-generator/utils/get-selected-form-fields';
import { useInitializationDataContext } from '../context/data-context';
import { FormFieldAddress, PrefixForm } from '../global-types/addresses';
import { config } from '../regional-config';
import { getRequired } from '../utils';
import { handleZipChange } from '../utils/address/handle-zip-change';
import { useUsStateOptions } from './use-us-state-options';
import { useFeatureFlags } from './use-feature-flags';

/**
 * Custom hook to generate form fields based on provided parameters.
 * @param formHandlers The form handlers object from react-hook-form.
 * @param formPrefix The prefix for the form fields' DOM IDs.
 * @param selectedFormFieldsNames The list of selected form field names.
 *     The structure of this parameter is an array containing objects with the following structure:
 *     {
 *         typeOfField: string, // The type of the form field, such as 'textfield', 'dropdown', or 'collapse'.
 *         name: string, // The unique identifier or name of the form field.
 *         nestedFields?: { typeOfField: string, name: string }[] // An array of nested fields, applicable only when typeOfField is 'collapse'.
 *     }
 * @returns The list of selected form fields with their configurations.
 */
export const useFormFieldGenerator = (
  formHandlers: UseFormReturn<FormFieldAddress>,
  formPrefix: string,
  selectedFormFieldsNames: FormFieldSelectedList[],
) => {
  const { t } = useTranslation();
  const shortFieldError = (min: number, max: number) => `${t('addressUtil.shortFieldError', { min, max })}`;
  const {
    initializedDataState: { isUS, isUK },
  } = useInitializationDataContext();
  const { usStateOptions } = useUsStateOptions();
  const { IS_ADDRESS_DISPLAY_NAME_ENABLED } = useFeatureFlags();

  const {
    formState: { errors, dirtyFields, defaultValues },
    trigger,
    setValue,
    watch,
  } = formHandlers;
  const isDisplayNameDirty = Boolean(dirtyFields?.display_name);
  const hasDisplayName =
    IS_ADDRESS_DISPLAY_NAME_ENABLED && selectedFormFieldsNames.some((field) => field.name === 'display_name');
  const displayName = hasDisplayName ? watch('display_name') : undefined;

  useEffect(() => {
    // reset/default values need the same inline feedback as typed values.
    if (hasDisplayName && !isDisplayNameDirty) void trigger('display_name');
  }, [displayName, hasDisplayName, isDisplayNameDirty, defaultValues, trigger]);

  const formFields: FormFields[] = [
    {
      typeOfField: FieldType.TEXTFIELD,
      name: 'first_name',
      label: `${t('addressForm.firstName')}`,
      registerOptions: {
        required: getRequired(PrefixForm.ADDRESS_FORM, 'firstName'),
        validate: validStringRule(t('addressUtil.invalidCharacterError')),
        maxLength: { value: 40, message: shortFieldError(2, 40) },
        minLength: { value: 2, message: shortFieldError(2, 40) },
      },
      domId: `${formPrefix}-form-first-name`,
      isError: Boolean(errors.first_name),
      helperText: errors?.first_name ? errors?.first_name?.message : '',
      addClass: styles['form-field'],
      testId: 'address_first_name',
      required: true,
      dropdownOptions: [],
    },
    {
      typeOfField: FieldType.TEXTFIELD,
      name: 'last_name',
      label: `${t('addressForm.lastName')}`,
      registerOptions: {
        required: getRequired(PrefixForm.ADDRESS_FORM, 'lastName'),
        validate: validStringRule(t('addressUtil.invalidCharacterError')),
        maxLength: { value: 40, message: shortFieldError(2, 40) },
        minLength: { value: 2, message: shortFieldError(2, 40) },
      },
      domId: `${formPrefix}-form-last-name`,
      isError: Boolean(errors.last_name),
      helperText: errors.last_name ? errors.last_name.message : '',
      addClass: styles['form-field'],
      testId: 'address_last_name',
      required: true,
      dropdownOptions: [],
    },
    {
      typeOfField: FieldType.TEXTFIELD,
      name: 'display_name',
      label: `${t('addressForm.displayName')}`,
      registerOptions: {
        validate: {
          maxLength: validDisplayNameLengthRule(t('addressUtil.displayNameLengthError')),
          characters: validDisplayNameRule(t('addressUtil.displayNameCharacterError')),
        },
      },
      domId: `${formPrefix}-form-display-name`,
      isError: Boolean(errors.display_name),
      helperText: errors.display_name ? errors.display_name.message : '',
      underMessage: `${t('addressForm.underMessage')}`,
      addClass: styles['form-field'],
      testId: 'address_display_name',
      required: false,
      dropdownOptions: [],
    },
    {
      typeOfField: FieldType.AUTOCOMPLETE,
      name: 'address_line_1',
      label: `${t('addressForm.addressLine1')}`,
      registerOptions: {
        required: getRequired(PrefixForm.ADDRESS_FORM, 'addressLine1'),
        maxLength: { value: 55, message: shortFieldError(1, 55) },
        minLength: { value: 1, message: shortFieldError(1, 55) },
      },
      domId: `${formPrefix}-form-address-line-1`,
      isError: Boolean(errors.address_line_1),
      helperText: errors.address_line_1 ? errors.address_line_1.message : '',
      addClass: styles['form-field'],
      testId: 'address_line_1',
      required: true,
      dropdownOptions: [],
    },
    {
      typeOfField: FieldType.TEXTFIELD,
      name: 'address_line_2',
      label: `${t('addressForm.addressLine2')}`,
      registerOptions: {
        maxLength: { value: 55, message: shortFieldError(1, 55) },
        minLength: { value: 1, message: shortFieldError(1, 55) },
      },
      domId: `${formPrefix}-form-address-line-2`,
      isError: Boolean(errors.address_line_2),
      helperText: errors.address_line_2 ? errors.address_line_2.message : '',
      addClass: styles['form-field'],
      testId: 'address_line_2',
      required: false,
      dropdownOptions: [],
    },
    {
      typeOfField: FieldType.TEXTFIELD,
      name: 'company_name',
      label: `${t('addressForm.companyName')}`,
      registerOptions: {
        maxLength: { value: 50, message: shortFieldError(0, 50) },
        minLength: { value: 0, message: shortFieldError(0, 50) },
      },
      domId: `${formPrefix}-form-company-name`,
      isError: Boolean(errors.company_name),
      helperText: errors.company_name ? errors.company_name.message : '',
      addClass: styles['form-field'],
      testId: 'company_name',
      required: false,
      dropdownOptions: [],
    },
    {
      typeOfField: FieldType.TEXTFIELD,
      name: 'city',
      label: `${t('addressForm.city')}`,
      registerOptions: {
        required: `${t('addressForm.isRequired', { requirement: 'City' })}`,
        validate: validStringRule(t('addressUtil.invalidCharacterError')),
        maxLength: { value: 26, message: shortFieldError(2, 26) },
        minLength: { value: 2, message: shortFieldError(2, 26) },
      },
      domId: `${formPrefix}-form-city`,
      isError: Boolean(errors.city),
      helperText: errors.city ? errors.city.message : '',
      addClass: styles['form-field'],
      testId: 'address_city',
      required: true,
      dropdownOptions: [],
    },
    {
      typeOfField: FieldType.DROPDOWN,
      name: 'state_code',
      label: t('addressForm.state'),
      registerOptions: {
        required: `${t('addressForm.isRequired', { requirement: 'State' })}`,
        maxLength: { value: 2, message: t('addressUtil.validStateCodeError') },
        minLength: { value: 2, message: t('addressUtil.validStateCodeError') },
      },
      domId: `${formPrefix}-form-state_code`,
      isError: Boolean(errors.state_code),
      helperText: errors.state_code ? errors.state_code.message : '',
      addClass: styles['form-field'],
      testId: 'address_state_code',
      required: true,
      showDropdown: isUS && usStateOptions.length > 0,
      dropdownOptions: [{ value: '', label: '' }, ...usStateOptions],
    },
    {
      typeOfField: FieldType.TEXTFIELD,
      name: 'zip',
      label: `${t('addressForm.zip')}`,
      registerOptions: {
        required: `${t('addressForm.isRequired', { requirement: isUK ? 'postcode' : 'ZIP code' })}`,
        validate: validZipCodeRule(t('addressUtil.validZipCodeError')),
        maxLength: { value: getThemeZipLength().max, message: t('addressUtil.validZipCodeError') },
        minLength: { value: getThemeZipLength().min, message: t('addressUtil.validZipCodeError') },
      },
      domId: `${formPrefix}-form-zip`,
      isError: Boolean(errors.zip),
      helperText: errors.zip ? errors.zip.message : '',
      addClass: styles['form-field'],
      testId: 'address_zip',
      required: true,
      onChange: (e: ChangeEvent<HTMLInputElement>) =>
        handleZipChange(config?.addressForm?.handleZipChange, trigger, e.target.value, setValue),
      dropdownOptions: [],
    },
    {
      typeOfField: FieldType.CHECKBOX,
      name: 'isQuickAddress',
      label: `${t('addressForm.saveToAddressBook')}`,
      required: false,
      domId: `${formPrefix}-form-isQuickAddress`,
      addClass: styles['checkbox'],
      testId: 'address_isQuickAddress',
      dropdownOptions: [],
      isError: false,
      registerOptions: {},
      showCheckbox: isUS,
    },
  ];

  // selectedFormFieldsList is an array of FormFields that is passed as a parameter to the FormFieldGenerator component
  const generatedFormFields = getSelectedFormFields(selectedFormFieldsNames, formFields);

  return { generatedFormFields };
};
